import React from 'react';
import { LeaseListItem, LeaseSearchParams } from './types';
import {
  PagerBar,
  SearchListBar,
  SearchCriterion,
  SearchableField,
  SortBar,
  SortOption,
  SortColumn,
  ListItemSection,
  LoadingBar,
  formatCurrency,
  formatDate,
  ListItemDisplayData
} from '../../../components/common';
import '../../../components/common/CRMList.css';

interface LeaseListProps {
  leases: LeaseListItem[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  selectedLeaseId?: number;
  onSelectLease: (lease: LeaseListItem) => void;
  onDeleteLease: (lease: LeaseListItem) => void;
  onSearch: (searchParams: LeaseSearchParams) => void;
  onCreateNew: () => void;
  globalLoading: boolean;
  loadingMessage: string;
}

const LeaseList: React.FC<LeaseListProps> = ({
  leases,
  loading,
  error,
  pagination,
  selectedLeaseId,
  onSelectLease,
  onDeleteLease,
  onSearch,
  onCreateNew,
  globalLoading,
  loadingMessage
}) => {
  const [searchValue, setSearchValue] = React.useState('');
  const [selectedCriteria, setSelectedCriteria] = React.useState<SearchCriterion[]>([]);
  const [currentSorts, setCurrentSorts] = React.useState<SortOption[]>([
    { field: 'renewalDate', direction: 'asc', label: 'Renewal Date' }
  ]);

  const searchableFields: SearchableField[] = [
    { key: 'property', label: 'Property', type: 'text' },
    { key: 'tenant', label: 'Tenant', type: 'text' },
    { key: 'address', label: 'Address', type: 'text' }
  ];

  const sortColumns: SortColumn[] = [
    { field: 'renewalDate', label: 'Renewal Date' },
    { field: 'dateCreated', label: 'Created Date' },
    { field: 'dateUpdated', label: 'Updated Date' },
    { field: 'property', label: 'Property' },
    { field: 'tenant', label: 'Tenant' }
  ];

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (selectedCriteria.length > 0) {
        onSearch({
          search: searchValue,
          fields: selectedCriteria.map((c) => c.field),
          page: 1
        });
      } else {
        onSearch({
          search: searchValue,
          fields: undefined,
          page: 1
        });
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchValue, selectedCriteria, onSearch]);

  const handleCriteriaChange = React.useCallback((criteria: SearchCriterion[]) => {
    setSelectedCriteria(criteria);
  }, []);

  const handleSortsChange = React.useCallback((sorts: SortOption[]) => {
    setCurrentSorts(sorts);

    if (sorts.length > 0) {
      onSearch({
        sorts: sorts.map((sort) => ({
          field: sort.field as 'renewalDate' | 'dateCreated' | 'dateUpdated' | 'property' | 'tenant',
          direction: sort.direction as 'asc' | 'desc'
        })),
        page: 1
      });
    } else {
      onSearch({ sorts: undefined, page: 1 });
    }
  }, [onSearch]);

  const getDisplayData = (lease: LeaseListItem): ListItemDisplayData => ({
    title: lease.propertyName || 'Unknown Property',
    category: lease.typeName || 'Lease',
    fields: [
      { name: 'address', value: lease.unitAddressLine || '—' },
      { name: 'tenants', value: lease.tenantNamesSummary || '—' },
      { name: 'amount', value: formatCurrency(lease.leaseAmount), isPrimary: true },
      { name: 'renewal', value: lease.renewalDate ? formatDate(lease.renewalDate) : '—' }
    ]
  });

  if (error) {
    return (
      <div className="list">
        <div className="list-header">
          <h3>Leases ({pagination.total})</h3>
          <button
            className="app-btn app-btn-primary list-btn-add-item"
            onClick={onCreateNew}
            title="Add New Lease"
          >
            +
          </button>
        </div>
        <div className="app-error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="list">
      <div className="list-header">
        <h3>Leases ({pagination.total})</h3>
        <button
          className="app-btn app-btn-primary list-btn-add-item"
          onClick={onCreateNew}
          title="Add New Lease"
        >
          +
        </button>
      </div>

      <SearchListBar
        searchableFields={searchableFields}
        searchValue={searchValue}
        selectedCriteria={selectedCriteria}
        onSearchChange={setSearchValue}
        onCriteriaChange={handleCriteriaChange}
        placeholder="Search leases..."
      />

      <SortBar
        availableColumns={sortColumns}
        currentSorts={currentSorts}
        onSortsChange={handleSortsChange}
        defaultSort={{ field: 'renewalDate', direction: 'asc', label: 'Renewal Date' }}
      />

      <ListItemSection
        items={leases}
        selectedItemId={selectedLeaseId}
        onSelectItem={onSelectLease}
        onDeleteItem={onDeleteLease}
        deleteConfirmMessage={(lease) =>
          `Are you sure you want to delete the lease for ${lease.propertyName}? This action cannot be undone.`
        }
        loading={loading}
        loadingMessage=""
        emptyMessage="No leases found. Try adjusting your search criteria."
        getDisplayData={getDisplayData}
      />

      <PagerBar
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        itemsPerPage={pagination.limit}
        onPageChange={(page) => onSearch({ page })}
        onItemsPerPageChange={(limit) => onSearch({ limit, page: 1 })}
      />

      <LoadingBar isLoading={globalLoading} message={loadingMessage} />
    </div>
  );
};

export default LeaseList;
